import { Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  paymentTermsSum,
  paymentTermsValid,
  type PaymentTermLine,
} from "@/lib/api"
import { cn } from "@/lib/utils"

/**
 * Editor de linhas de condições de pagamento: percentagem + descrição, com
 * adicionar/remover. Mostra a soma e sinaliza quando não fecha nos 100%.
 * Reutilizado nas Configurações (presets) e no Editar da proposta.
 */
export function PaymentTermsLines({
  lines,
  onChange,
  disabled,
}: {
  lines: PaymentTermLine[]
  onChange: (next: PaymentTermLine[]) => void
  disabled?: boolean
}) {
  const sum = paymentTermsSum(lines)
  const valid = paymentTermsValid(lines)

  function patch(index: number, p: Partial<PaymentTermLine>) {
    onChange(lines.map((l, i) => (i === index ? { ...l, ...p } : l)))
  }

  function remove(index: number) {
    onChange(lines.filter((_, i) => i !== index))
  }

  function add() {
    onChange([...lines, { pct: 0, label: "" }])
  }

  return (
    <div className="space-y-2">
      {lines.map((line, i) => (
        <div key={i} className="flex items-end gap-2">
          <div className="w-24">
            {i === 0 && <Label className="text-xs">%</Label>}
            <Input
              type="number"
              step="0.5"
              min={0}
              max={100}
              value={line.pct}
              disabled={disabled}
              onChange={(e) =>
                patch(i, { pct: Math.max(0, Number(e.target.value) || 0) })
              }
              className="h-9 text-right"
            />
          </div>
          <div className="flex-1">
            {i === 0 && <Label className="text-xs">Descrição</Label>}
            <Input
              value={line.label}
              disabled={disabled}
              placeholder="Ex.: Adjudicação"
              onChange={(e) => patch(i, { label: e.target.value })}
              className="h-9"
            />
          </div>
          <button
            type="button"
            aria-label="Remover linha"
            disabled={disabled || lines.length <= 1}
            onClick={() => remove(i)}
            className="mb-1 rounded p-2 text-destructive hover:bg-destructive/10 disabled:opacity-30"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}

      <div className="flex items-center justify-between pt-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={add}
          disabled={disabled}
        >
          <Plus className="h-4 w-4" />
          Adicionar linha
        </Button>
        <span
          className={cn(
            "rounded px-2 py-0.5 text-xs font-semibold",
            valid
              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
              : "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
          )}
        >
          Total: {sum.toFixed(1)}%{valid ? "" : " — tem de somar 100%"}
        </span>
      </div>
    </div>
  )
}
